import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const industries = [
  "Restauración", "Talleres y automoción", "Clínicas y salud", "Despachos y servicios profesionales",
  "Comercio y retail", "Ecommerce", "Construcción e inmobiliario", "Industria", "Logística", "Otro",
];
const employeeBands = ["1–5", "6–10", "11–50", "51–200", "200+"];
const contactMethods = ["Email", "Teléfono", "WhatsApp", "Videollamada"];

const inquiryOptions = [
  { value: "Solicitar un diagnóstico inicial", key: "diagnostico" },
  { value: "Consulta general", key: "consulta" },
];

const fieldWrap = "flex flex-col gap-2";
const labelCls = "text-xs uppercase tracking-[0.18em] text-white/50";
const inputCls =
  "w-full border-b border-white/20 bg-transparent py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-white transition-colors text-lg";
const triggerCls =
  "w-full border-0 border-b border-white/20 rounded-none bg-transparent text-white text-lg px-0 py-3 h-auto focus:ring-0 data-[placeholder]:text-white/30";

export const ContactForm = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    company: "", name: "", email: "", phone: "",
    industry: "", employees: "", challenge: "", preferred_contact: "",
    inquiry_type: "Solicitar un diagnóstico inicial",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.name || !form.email || !form.challenge) {
      toast.error("Por favor, completa empresa, nombre, email y tu reto.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      navigate("/gracias");
    } catch (err) {
      toast.error("Se ha producido un error. Inténtalo de nuevo.");
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="contact-form" className="space-y-8">
      <div className="flex flex-wrap gap-3" data-testid="inquiry-toggle">
        {inquiryOptions.map((t) => (
          <button
            key={t.key}
            type="button"
            data-testid={`inquiry-${t.key}`}
            onClick={() => set("inquiry_type")(t.value)}
            className={`rounded-full px-5 py-2.5 text-sm transition-all ${
              form.inquiry_type === t.value
                ? "bg-white text-[#0B1F3A]"
                : "border border-white/25 text-white/70 hover:border-white"
            }`}
          >
            {t.value}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7">
        <div className={fieldWrap}>
          <label className={labelCls}>Empresa</label>
          <input data-testid="input-company" className={inputCls} placeholder="Tu empresa" value={form.company} onChange={set("company")} />
        </div>
        <div className={fieldWrap}>
          <label className={labelCls}>Nombre</label>
          <input data-testid="input-name" className={inputCls} placeholder="Tu nombre" value={form.name} onChange={set("name")} />
        </div>
        <div className={fieldWrap}>
          <label className={labelCls}>Email</label>
          <input data-testid="input-email" type="email" className={inputCls} placeholder="tu@empresa.com" value={form.email} onChange={set("email")} />
        </div>
        <div className={fieldWrap}>
          <label className={labelCls}>Teléfono</label>
          <input data-testid="input-phone" className={inputCls} placeholder="+34 000 000 000" value={form.phone} onChange={set("phone")} />
        </div>

        <div className={fieldWrap}>
          <label className={labelCls}>Sector</label>
          <Select value={form.industry} onValueChange={set("industry")}>
            <SelectTrigger data-testid="select-industry" className={triggerCls}>
              <SelectValue placeholder="Selecciona un sector" />
            </SelectTrigger>
            <SelectContent>
              {industries.map((i) => (
                <SelectItem key={i} value={i} data-testid={`industry-${i}`}>{i}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className={fieldWrap}>
          <label className={labelCls}>Empleados</label>
          <Select value={form.employees} onValueChange={set("employees")}>
            <SelectTrigger data-testid="select-employees" className={triggerCls}>
              <SelectValue placeholder="Tamaño del equipo" />
            </SelectTrigger>
            <SelectContent>
              {employeeBands.map((b) => (
                <SelectItem key={b} value={b} data-testid={`employees-${b}`}>{b}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className={fieldWrap}>
        <label className={labelCls}>Cómo prefieres que te contactemos</label>
        <Select value={form.preferred_contact} onValueChange={set("preferred_contact")}>
          <SelectTrigger data-testid="select-contact-method" className={triggerCls}>
            <SelectValue placeholder="Selecciona un método de contacto" />
          </SelectTrigger>
          <SelectContent>
            {contactMethods.map((m) => (
              <SelectItem key={m} value={m} data-testid={`contact-method-${m}`}>{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className={fieldWrap}>
        <label className={labelCls}>Tu reto actual</label>
        <textarea data-testid="input-challenge" rows={3} className={`${inputCls} resize-none`} placeholder="Cuéntanos qué te gustaría mejorar o resolver…" value={form.challenge} onChange={set("challenge")} />
      </div>

      <button
        type="submit"
        data-testid="contact-submit"
        disabled={loading}
        className="inline-flex items-center gap-2 rounded-full bg-white text-[#0B1F3A] px-8 py-4 font-medium transition-all duration-300 hover:scale-[1.02] disabled:opacity-60"
      >
        {loading ? <><Loader2 size={18} className="animate-spin" /> Enviando…</> : <>Enviar solicitud <ArrowUpRight size={18} /></>}
      </button>
    </form>
  );
};

export default ContactForm;
